import { useMemo, useState } from 'react';
import { Alert, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import Badge from '../base/Badge';
import Button from '../base/Button';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableServerFooter from '../base/AdvanceTableServerFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';

import { fetchCompletedOrders } from '../../features/orders/ordersApi';
import { clientColour } from '../../helpers/clientColour';
import { formatLength } from '../../helpers/formatLength';

/**
 * Orders that are finished, newest first.
 *
 * The mirror of the projects panel, and for the same reason: an order leaves
 * Redis once it is done and lands in the Mongo archive, so the only screen that
 * ever showed it was the one listing live work — which by definition no longer
 * has it.
 *
 * Paged on the SERVER. This list only grows, and the backend caps the page size
 * whatever the client asks for. See CompletedProjectsPanel for why the totals in
 * the footer cannot come from the table's own row model.
 */

const PAGE_SIZE = 25;

const when = ts => {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium' }).format(d);
};

const CompletedOrdersPanel = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['orders', 'completed', page, q],
    queryFn: () => fetchCompletedOrders({ page, limit: PAGE_SIZE, q }),
    placeholderData: keepPreviousData,
    staleTime: 10_000
  });

  const rows = useMemo(() => data?.orders ?? [], [data]);
  const pages = data?.pages ?? 1;
  const total = data?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'orderCode',
        header: 'Order',
        enableSorting: false,
        cell: ({ row: { original: o } }) => (
          <span className="fw-semibold">{o.orderCode || o.orderId}</span>
        )
      },
      {
        accessorKey: 'client',
        header: 'Customer',
        enableSorting: false,
        cell: ({ row: { original: o } }) =>
          o.client ? (
            <Badge bg={clientColour(o.client)} variant="phoenix" className="fs-10">
              {o.client}
            </Badge>
          ) : (
            <span className="text-700">—</span>
          )
      },
      {
        accessorKey: 'orderedLength',
        header: 'Ordered',
        enableSorting: false,
        cell: ({ row: { original: o } }) => formatLength(o.orderedLength),
        meta: { headerProps: { className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'producedLength',
        header: 'Delivered',
        enableSorting: false,
        cell: ({ row: { original: o } }) => formatLength(o.producedLength),
        meta: { headerProps: { className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'createdAt',
        header: 'Placed',
        enableSorting: false,
        cell: ({ row: { original: o } }) => when(o.createdAt),
        meta: { cellProps: { className: 'white-space-nowrap' } }
      },
      {
        accessorKey: 'closedAt',
        header: 'Closed',
        enableSorting: false,
        cell: ({ row: { original: o } }) => when(o.closedAt),
        meta: { cellProps: { className: 'white-space-nowrap' } }
      },
      {
        id: 'where',
        header: 'Where',
        enableSorting: false,
        cell: ({ row: { original: o } }) => (
          <Badge bg={o.archived ? 'info' : 'success'} variant="phoenix" className="fs-10">
            {o.archived ? 'Archived' : 'Still in Redis'}
          </Badge>
        )
      },
      {
        id: 'open',
        header: '',
        enableSorting: false,
        cell: ({ row: { original: o } }) => (
          <Link
            to={`/apps/sales-management/order-details/${o.orderId}`}
            className="btn btn-phoenix-secondary btn-sm py-0 px-2 fs-10"
            title="Open this order"
            onClick={e => e.stopPropagation()}
          >
            Open
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-1 fs-10" />
          </Link>
        ),
        meta: { headerProps: { style: { width: 90 } }, cellProps: { className: 'text-end' } }
      }
    ],
    []
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: PAGE_SIZE,
    pagination: true,
    sortable: false,
    manualPagination: true,
    pageCount: pages
  });

  const submit = e => {
    e.preventDefault();
    setPage(1);
    setQ(search.trim());
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="text-1100 mb-0">
              Completed <span className="fw-normal text-700 fs-7">({total.toLocaleString()})</span>
            </h3>
            <p className="text-700 fs-9 mb-0">
              Most recently finished first. Click a row to open it.
              {data?.counts?.archived > 0 && (
                <span className="text-700">
                  {' '}
                  {data.counts.archived.toLocaleString()} archived,{' '}
                  {data.counts.live} still open in production storage.
                </span>
              )}
            </p>
          </div>

          <Form onSubmit={submit}>
            <InputGroup style={{ maxWidth: 320 }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faMagnifyingGlass} className="fs-10" />
              </InputGroup.Text>
              <Form.Control
                size="sm"
                placeholder="order code or customer…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <Button type="submit" size="sm" variant="phoenix-secondary">
                Search
              </Button>
            </InputGroup>
          </Form>
        </div>

        {isError && (
          <Alert variant="danger" className="py-2 fs-9">
            {error?.response?.data?.error || error?.message || 'Could not load completed orders'}
          </Alert>
        )}

        {isLoading ? (
          <div className="d-flex justify-content-center py-6">
            <Spinner animation="border" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-700">
            {q ? `No completed order matches “${q}”.` : 'No order has been completed yet.'}
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{ size: 'sm', className: 'phoenix-table fs-9 mb-0' }}
              rowClassName="align-middle"
              getRowProps={row => ({
                style: { cursor: 'pointer' },
                onClick: () =>
                  navigate(`/apps/sales-management/order-details/${row.original.orderId}`)
              })}
            />
            <AdvanceTableServerFooter
              className="mt-3"
              page={page}
              pages={pages}
              total={total}
              pageSize={PAGE_SIZE}
              onPage={setPage}
              isFetching={isFetching}
            />
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default CompletedOrdersPanel;
